import { Children, isValidElement, type ReactNode } from 'react';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Divider,
  Group,
  Paper,
  Text,
  UnstyledButton,
} from '../../../core';
import { SURFACE_COLORS } from '../surface-colors';

export interface ArtifactGroupProps {
  /** Section label, e.g. `Generated spreadsheets`. */
  readonly label?: string;
  /** Pre-formatted file count, e.g. `2 files`. Omitted for a single file. */
  readonly countLabel?: string;
  /** Pre-formatted total, e.g. `9.6 KB`. */
  readonly totalSizeLabel?: string;
  /** One or more ArtifactRow elements — the agent controls how many. */
  readonly children: ReactNode;
  readonly onDownloadAll?: () => void | Promise<void>;
}

/** Groups every file produced by one tool call into a single bordered section.
 *
 *  Replaces the previous one-standalone-card-per-file layout, which rendered N
 *  bordered boxes each with its own full-width primary CTA — visual weight out of
 *  all proportion to a download affordance, and no way to see a total.
 *
 *  Count and total size arrive pre-formatted from the agent: each child's size
 *  lives in its own data-model node, so the group can't see them, and the A2UI
 *  guidance is to pre-compute display values agent-side. */
export function ArtifactGroup({
  label,
  countLabel,
  totalSizeLabel,
  children,
  onDownloadAll,
}: ArtifactGroupProps) {
  const meta = [countLabel, totalSizeLabel].filter(Boolean).join(' · ');
  // Rows must read as discrete files, so they get a hairline between them —
  // never above the first or below the last, where the section border already
  // does that job. Interleaving here (rather than a border on the row itself)
  // keeps the row unaware of its own position.
  const rows = Children.toArray(children).filter(Boolean);

  return (
    <Paper
      withBorder
      radius={12}
      // Fills the message column like every other chat surface. An explicit
      // max-width made it visibly narrower than its neighbours and squeezed the
      // filename into truncating far earlier than it needed to.
      style={{
        width: '100%',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      {(label || meta) && (
        <>
          <Group
            justify="space-between"
            wrap="nowrap"
            style={{ gap: 12, padding: '6px 12px' }}
          >
            <Text
              fw={500}
              c="dimmed"
              tt="uppercase"
              truncate
              style={{
                fontSize: 11,
                lineHeight: '16px',
                letterSpacing: '0.06em',
              }}
            >
              {label || 'Generated files'}
            </Text>
            {meta && (
              <Text
                c="dimmed"
                style={{
                  flexShrink: 0,
                  fontSize: 11,
                  lineHeight: '16px',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {meta}
              </Text>
            )}
          </Group>
          <Divider />
        </>
      )}

      {rows.map((row, i) => (
        // Children.toArray assigns each entry a stable key, so reuse it rather
        // than keying on the array index.
        <Box key={isValidElement(row) ? row.key : i}>
          {i > 0 && <Divider />}
          {row}
        </Box>
      ))}

      {onDownloadAll && (
        <>
          <Divider />
          {/* Faintly tinted so the footer reads as a section action rather than
              another file row. */}
          <Box
            style={{
              padding: '6px 12px',
              backgroundColor: SURFACE_COLORS.inset,
            }}
          >
            <UnstyledButton
              onClick={() => {
                // Swallow rejections so they can't surface as an unhandled
                // promise; the host reports its own download errors.
                Promise.resolve(onDownloadAll()).catch(() => undefined);
              }}
            >
              <Group wrap="nowrap" style={{ gap: 6 }}>
                <FontAwesomeIcon
                  icon={faArrowDownToLine}
                  style={{ fontSize: 14 }}
                />
                <Text fw={500} style={{ fontSize: 12, lineHeight: '16px' }}>
                  Download all
                </Text>
                {totalSizeLabel && (
                  <Text c="dimmed" style={{ fontSize: 12, lineHeight: '16px' }}>
                    ({totalSizeLabel})
                  </Text>
                )}
              </Group>
            </UnstyledButton>
          </Box>
        </>
      )}
    </Paper>
  );
}
